/**
 * Reine Logik der SiFa-Betriebsdaten (docs/sifa-betreuung-plan.md, Paket A).
 *
 * Kein React, kein Supabase: Vorschlaege aus DGUV Vorschrift 2 / DGUV
 * Vorschrift 1, die Anzeigezahlen der Betreuungskarte und der Statuswechsel
 * (D1-Fix), damit alles ohne jsdom testbar bleibt.
 */
import { IntervalConfig, ProjectGlobalData, ProjectStatus, SifaBetreuungsgruppe } from '../../types';

/**
 * Grundbetreuung je Beschaeftigtem und Jahr nach DGUV Vorschrift 2, Anlage 2.
 * Die Summe gilt fuer SiFa UND Betriebsarzt zusammen — deshalb ist das Ergebnis
 * nur ein Vorschlag, der SiFa-Anteil bleibt Vereinbarung.
 */
export const EINSATZZEIT_JE_BESCHAEFTIGTEM: Record<SifaBetreuungsgruppe, number> = {
  I: 2.5,
  II: 1.5,
  III: 0.5,
};

export const BETREUUNGSGRUPPEN: ReadonlyArray<{ id: SifaBetreuungsgruppe; label: string; hilfe: string }> = [
  { id: 'I', label: 'Gruppe I', hilfe: '2,5 h je Beschäftigtem und Jahr — hohe Gefährdung (z. B. Bau, Metall, Chemie)' },
  { id: 'II', label: 'Gruppe II', hilfe: '1,5 h je Beschäftigtem und Jahr — mittlere Gefährdung (z. B. Handwerk, Pflege, Logistik)' },
  { id: 'III', label: 'Gruppe III', hilfe: '0,5 h je Beschäftigtem und Jahr — geringe Gefährdung (z. B. Büro, Verwaltung, Handel)' },
];

/**
 * Vorschlag der Einsatzzeit (h/Jahr) = Beschaeftigte × Satz der Gruppe,
 * auf ganze Stunden gerundet. null, wenn Gruppe oder Beschaeftigte fehlen.
 */
export const vorschlagEinsatzzeit = (
  gruppe: SifaBetreuungsgruppe | undefined,
  beschaeftigte: number | undefined,
): number | null => {
  if (!gruppe || !(gruppe in EINSATZZEIT_JE_BESCHAEFTIGTEM)) return null;
  if (beschaeftigte === undefined || !Number.isFinite(beschaeftigte) || beschaeftigte <= 0) return null;
  return Math.round(beschaeftigte * EINSATZZEIT_JE_BESCHAEFTIGTEM[gruppe]);
};

/** Standardquote fuer das Ersthelfer-Soll (DGUV Vorschrift 1 § 26). */
export const ERSTHELFER_QUOTE_STANDARD = 0.05;

/**
 * Ersthelfer-Soll nach DGUV Vorschrift 1 § 26: bis 20 Beschaeftigte einer,
 * darueber ein Prozentsatz der Beschaeftigten (5 % in Verwaltungs- und
 * Handelsbetrieben, 10 % in sonstigen Betrieben), aufgerundet, mindestens
 * einer. Standard hier: 5 % — die Karte nennt die Alternative im Hinweistext.
 * null, wenn keine Beschaeftigtenzahl vorliegt.
 */
export const ersthelferSoll = (
  beschaeftigte: number | undefined,
  quote: number = ERSTHELFER_QUOTE_STANDARD,
): number | null => {
  if (beschaeftigte === undefined || !Number.isFinite(beschaeftigte) || beschaeftigte <= 0) return null;
  if (beschaeftigte <= 20) return 1;
  return Math.max(1, Math.ceil(beschaeftigte * quote));
};

/** ASA-Pflicht (§ 11 ASiG): Betriebe mit mehr als 20 Beschaeftigten. */
export const asaPflichtig = (beschaeftigte: number | undefined): boolean =>
  beschaeftigte !== undefined && Number.isFinite(beschaeftigte) && beschaeftigte > 20;

/** Einsatzzeit-Fortschritt fuer die Betreuungskarte: Minuten des Jahres gegen das Soll in Stunden. */
export const einsatzzeitFortschritt = (
  minutenJahr: number,
  sollStunden: number | undefined,
): { erbrachtStunden: number; sollStunden: number | null; prozent: number | null } => {
  const erbrachtStunden = Math.round((Math.max(0, minutenJahr || 0) / 60) * 10) / 10;
  if (sollStunden === undefined || !Number.isFinite(sollStunden) || sollStunden <= 0) {
    return { erbrachtStunden, sollStunden: null, prozent: null };
  }
  return {
    erbrachtStunden,
    sollStunden,
    prozent: Math.min(100, Math.round((erbrachtStunden / sollStunden) * 100)),
  };
};

/** Zahlfeld aus einem Eingabetext: leer/ungueltig → undefined (Feld bleibt frei). */
export const zahlAusEingabe = (text: string): number | undefined => {
  const t = (text ?? '').trim().replace(',', '.');
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

/**
 * Statuswechsel im Hub (ProjectStats.applyStatusChange) — der D1-Fix.
 *
 * SiGeKo/HSE (unveraendert): Planung → Ausfuehrung setzt Projektstart und den
 * naechsten Faelligkeitstermin auf heute; `intervalConfig` wird immer
 * mitgeschrieben.
 *
 * SiFa: `intervalConfig` wird NICHT angefasst — `schreibeIntervalConfig` ist
 * false, der Aufrufer laesst den Schluessel im JSON exakt, wie er war
 * (undefined bleibt undefined, kein `{}`). Der Betreuungsbeginn wird beim
 * Wechsel auf „Laufend" nur gesetzt, wenn er noch leer ist.
 */
export interface StatusWechselPlan {
  globalDataPatch: Partial<ProjectGlobalData> & { status: ProjectStatus };
  schreibeIntervalConfig: boolean;
  intervalConfig?: IntervalConfig;
}

export const planeStatusWechsel = (args: {
  istSifa: boolean;
  bisherigerStatus: ProjectStatus;
  neuerStatus: ProjectStatus;
  startDate: string | undefined;
  intervalConfig: IntervalConfig | undefined;
  heute: string;
}): StatusWechselPlan => {
  const { istSifa, bisherigerStatus, neuerStatus, startDate, intervalConfig, heute } = args;
  const globalDataPatch: StatusWechselPlan['globalDataPatch'] = { status: neuerStatus };
  if (neuerStatus === 'on_hold') globalDataPatch.onHoldUntil = '';

  if (istSifa) {
    if (neuerStatus === 'active' && !startDate) globalDataPatch.startDate = heute;
    return { globalDataPatch, schreibeIntervalConfig: false };
  }

  const nextIntervalConfig: IntervalConfig = { ...(intervalConfig ?? { type: 'weekly' }) };
  if (bisherigerStatus === 'planning' && neuerStatus === 'active') {
    globalDataPatch.startDate = heute;
    nextIntervalConfig.nextScheduledDate = heute;
  }
  return { globalDataPatch, schreibeIntervalConfig: true, intervalConfig: nextIntervalConfig };
};

/**
 * Statusumschalter der Betreuungskarte: keine Planungsphase bei SiFa.
 * „Archiv" haengt die Karte selbst an — nur fuer Administratoren aus
 * „Beendet" heraus, oder wenn das Projekt schon dort liegt.
 */
export const SIFA_STATUS_OPTIONEN: ReadonlyArray<{ id: ProjectStatus; label: string }> = [
  { id: 'active', label: 'Laufend' },
  { id: 'on_hold', label: 'Ruht' },
  { id: 'completed', label: 'Beendet' },
];

export const SIFA_ARCHIV_OPTION: { id: ProjectStatus; label: string } = { id: 'finished', label: 'Archiv' };

export const SIFA_STATUS_LABELS: Record<ProjectStatus, string> = {
  planning: 'Vorbereitung',
  active: 'Laufende Betreuung',
  on_hold: 'Ruht',
  completed: 'Beendet',
  finished: 'Archiv',
};
